import { gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';

import { EReactionType } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { comment_liked, comment_unliked } from '@/analytics';

import { useCommentLike_CommentFragment } from './graphql-types/useCommentLike_CommentFragment';
import {
  useCommentLike_LikeComment,
  useCommentLike_LikeCommentVariables,
} from './graphql-types/useCommentLike_LikeComment';
import {
  useCommentLike_UnlikeComment,
  useCommentLike_UnlikeCommentVariables,
} from './graphql-types/useCommentLike_UnlikeComment';

const COMMENT_FRAGMENT = gql`
  fragment useCommentLike_CommentFragment on Comment {
    _id
    reactionCount
    reactions {
      _id
      user {
        _id
        displayName
      }
    }
    currentUserReaction {
      _id
      reactionType
    }
    postV2 {
      _id
      tags {
        _id
      }
      announcement {
        _id
      }
      sponsorData {
        audience {
          group {
            _id
          }
        }
      }
      group {
        _id
        isGlobalSingleton
      }
      organization {
        _id
      }
      event {
        _id
      }
    }
  }
`;

const LIKE_COMMENT = gql`
  mutation useCommentLike_LikeComment($input: CreateCommentReactionInput!) {
    createCommentReaction(input: $input) {
      data {
        _id
        reactionType
        comment {
          _id
          ...useCommentLike_CommentFragment
        }
      }
    }
  }
  ${COMMENT_FRAGMENT}
`;

const UNLIKE_COMMENT = gql`
  mutation useCommentLike_UnlikeComment($commentId: String!) {
    deleteCommentReaction(input: { commentId: $commentId }) {
      data {
        _id
        reactionType
        comment {
          _id
          ...useCommentLike_CommentFragment
        }
      }
    }
  }
  ${COMMENT_FRAGMENT}
`;

export const useCommentLike = (
  comment: useCommentLike_CommentFragment
): {
  handleLikeClick: () => Promise<void>;
  isLoading: boolean;
} => {
  const toast = useToast();
  const [likeComment, { loading: likeCommentLoading }] = useMutation<
    useCommentLike_LikeComment,
    useCommentLike_LikeCommentVariables
  >(LIKE_COMMENT);

  const [unlikeComment, { loading: unlikeCommentLoading }] = useMutation<
    useCommentLike_UnlikeComment,
    useCommentLike_UnlikeCommentVariables
  >(UNLIKE_COMMENT);
  const post = comment.postV2;
  const postType =
    post?.__typename === 'AnswerV2'
      ? 'answer'
      : post?.__typename === 'QuestionV2'
        ? 'question'
        : post?.__typename === 'BlogV2'
          ? 'article'
          : post?.__typename === 'EventAlert'
            ? 'eventPublished'
            : post?.__typename === 'PollV2'
              ? 'poll'
              : 'message';
  const locationProperties = {
    post_type: postType,
    location_posted_in: post?.event
      ? 'event'
      : post?.organization
        ? 'organization'
        : post?.group?.isGlobalSingleton
          ? 'home'
          : 'group',
    location_event_id: post?.event?._id,
    location_organization_id: post?.organization?._id,
    location_group_id:
      !post?.organization && !post?.event ? post?.group?._id : undefined,
  };

  const handleLikeClick = async (): Promise<void> => {
    try {
      if (comment.currentUserReaction) {
        await unlikeComment({
          variables: {
            commentId: comment._id,
          },
          optimisticResponse: {
            deleteCommentReaction: {
              __typename: 'DeleteCommentReactionOutput',
              data: {
                __typename: 'Reaction',
                _id: 'temp-id-comment-unlike',
                reactionType: 'LIKE',
                comment: {
                  ...comment,
                  reactionCount: comment.reactionCount - 1,
                  currentUserReaction: {
                    __typename: 'Reaction',
                    _id: 'temp-id-comment-unlike',
                    reactionType: 'LIKE',
                  },
                },
              },
            },
          },
        });
        comment_unliked({
          entity_id: comment._id,
          entity_type: 'comment',
          parent_id: comment.postV2?._id || '',
          parent_type: 'post',
          reaction_target_id: comment._id,
          reaction_target_type: 'comment',
          reaction_type: comment.currentUserReaction.reactionType.toLowerCase(),
          reaction_action: 'unliked',
          ...locationProperties,
        });
      } else {
        await likeComment({
          variables: {
            input: { commentId: comment._id, reactionType: EReactionType.LIKE },
          },
          optimisticResponse: {
            createCommentReaction: {
              __typename: 'CreateCommentReactionOutput',
              data: {
                __typename: 'Reaction',
                _id: 'temp-id-comment-like',
                reactionType: 'LIKE',
                comment: {
                  ...comment,
                  reactionCount: comment.reactionCount + 1,
                  currentUserReaction: {
                    __typename: 'Reaction',
                    _id: 'temp-id-comment-like',
                    reactionType: 'LIKE',
                  },
                },
              },
            },
          },
        });

        // Track comment like with meaningful context
        comment_liked({
          entity_id: comment._id,
          entity_type: 'comment',
          parent_id: comment.postV2?._id || '',
          parent_type: 'post',
          reaction_target_id: comment._id,
          reaction_target_type: 'comment',
          reaction_type: 'like',
          reaction_action: 'liked',
          engagement_reaction_type: 'like',
          ...locationProperties,
        });
      }
    } catch (err) {
      logError(err);
      toast({
        description: 'Failed to update reaction on comment',
        status: 'error',
      });
    }
  };

  return {
    handleLikeClick,
    isLoading: likeCommentLoading || unlikeCommentLoading,
  };
};

useCommentLike.commentFragment = COMMENT_FRAGMENT;
